import { invoiceStatusLabel, invoiceStatusTagTone } from '@auctisync/api-client';
import { LearnMoreLink } from '@auctisync/ui';
import { formatCents, type Tables } from '@auctisync/shared';
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

type Invoice = Tables<'invoices'>;
type InvoiceWithAuction = Invoice & {
  auctions: Pick<Tables<'auctions'>, 'id' | 'title' | 'slug'> | null;
};

/**
 * /my/invoices — list of a bidder's invoices across all tenants. Sorted
 * newest-first. RLS ensures only the bidder's own rows come back.
 *
 * 🔴 STYLED BY THE SITE'S OWN CLASSES, NOT BY INLINE OBJECTS (branding round 3,
 * 2026-09-16). This page used `components/refined` and three local style objects
 * (`tableStyle` / `thStyle` / `tdStyle`). Inline styles cannot be reached by a
 * token change, a theme, dark mode, a preset or a branding round, which is
 * exactly why this screen kept looking unrelated to the rest of the site. The
 * table is `.wr-tblwrap` > `.wr-tbl` now — the same shell the lot list uses.
 *
 * ⚠️ EVERY `<td>` CARRIES `data-l`. `.wr-tbl` has a narrow-width mode that hides
 * the header row and relabels each cell from that attribute; without it the
 * table collapses into unlabelled fragments on a phone.
 */
export function BidderInvoices() {
  const { session, loading: sessionLoading, userId } = useSession();
  const [rows, setRows] = useState<InvoiceWithAuction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId === null) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error: err } = await supabase
        .from('invoices')
        .select('*, auctions!invoices_auction_id_fkey(id, title, slug)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (err) setError(err.message);
      else setRows((data ?? []) as InvoiceWithAuction[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (sessionLoading) return <p className="muted">Loading…</p>;
  if (!session) return <Navigate to="/admin" replace />;

  return (
    <>
      <div className="wa-hd">
        <div>
          <span className="w-lbl">Billing</span>
          <h1 className="w-h1" style={{ fontSize: 32 }}>
            My invoices
          </h1>
          <p className="muted" style={{ fontSize: 14 }}>
            One invoice per auction you won lots on. Click an invoice to pay or review the detail.
          </p>
        </div>
      </div>

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      {loading ? (
        <p className="muted">Loading invoices…</p>
      ) : rows.length === 0 ? (
        <div
          style={{
            padding: '1.25rem',
            borderRadius: 'var(--r-card, 0.5rem)',
            border: '1px dashed var(--border-strong)',
            color: 'var(--text-muted)',
          }}
        >
          <p style={{ margin: 0 }}>You don't have any invoices yet.</p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
            Invoices are created automatically when an auction closes and you've won lots.{' '}
            <LearnMoreLink helpSlug="paying-your-invoice" newTab>
              How invoicing works
            </LearnMoreLink>
          </p>
        </div>
      ) : (
        <div className="wr-tblwrap">
          <table className="wr-tbl">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Auction</th>
                <th>Total</th>
                <th>Status</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td data-l="Invoice #">
                    <code className="mono" style={{ fontSize: '0.85rem' }}>
                      {r.invoice_number ?? '—'}
                    </code>
                  </td>
                  <td data-l="Auction">{r.auctions?.title ?? '(unknown)'}</td>
                  <td data-l="Total">
                    <b className="mono">{formatCents(r.total_cents ?? 0)}</b>
                  </td>
                  <td data-l="Status">
                    <StatusBadge status={r.status} />
                  </td>
                  <td data-l="Created" className="muted" style={{ fontSize: '0.85rem' }}>
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                  <td data-l="" style={{ textAlign: 'right' }}>
                    <Link to={`/my/invoices/${r.id}`} style={{ fontSize: '0.9rem' }}>
                      {r.status === 'issued' || r.status === 'partially_paid' ? 'Pay' : 'View'} →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * Shared with the native app and the dashboard via `@auctisync/api-client`
 * (P2.7). This previously rendered the RAW column value for `void` and
 * `refunded` — database vocabulary shown to a bidder about their own bill.
 *
 * The WORDING still comes from the shared client; only the shape it is drawn in
 * changed, from `refined`'s `Pill` to the site's own `.tag`.
 */
function StatusBadge({ status }: { status: string }) {
  /* 🔴 THE MAPPING MOVED INTO `@auctisync/api-client` ON 2026-09-22. It was a
     ternary here and nowhere else, so when the phone grew a shared `Tag` it
     made its own — and chose green for `due`, on money still owed. One rule,
     two surfaces; see `invoiceStatusTagTone`. The CLASSES stay the site's own
     vocabulary, because those are a stylesheet's business, not a client's. */
  const CLASS: Record<ReturnType<typeof invoiceStatusTagTone>, string> = {
    noReserve: 't-nr',
    live: 't-live',
    neutral: 't-n',
  };
  return (
    <span className={`tag ${CLASS[invoiceStatusTagTone(status)]}`}>
      {invoiceStatusLabel(status)}
    </span>
  );
}

/*
 * The one style object left, and it is here on purpose: the bidder site has no
 * error treatment of its own yet, and round 3's brief was explicit — keep these
 * semantics, do not invent a class for them. When the site grows a `.wa-err`,
 * this goes with it.
 */
const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-control, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  marginBottom: '1rem',
};
